import { $t } from '@/i18n';
import { defineComponent, onMounted, onUnmounted, ref } from '@vue/composition-api';
import { jsonToYaml } from '@/utils/merge/jsonToYaml';
import { useRouter } from '@/router/useRouter';
import { aiMergeEditorRoutePath } from '@/router/route-const';
import { BASE_PATH } from '@/config/constant';
import { BuildingBlocksAnimation } from '@/pages/project/project-generator/loading';
const AI_MERGE_HOST_SOURCE = 'ai-editor-merge-host';
/**
 * AI 冲突编辑器视图（新版 xy）
 *
 * - deferSave=1：gogo「保存」更新 feat tip，发 conflictResolved + mergeSuccess；
 *   xy 提交前仍可 requestSave（幂等再落盘）
 * - keep-alive：iframe src 冻结，mergeSuccess / conflictResolved 只更新父页侧栏
 * - conflictResolved：乐观 resolved + awaitingProjectInfo，不回写 after；
 *   mergeSuccess 带 projectInfo 后再更新 after 并露出代码变更
 * - 父页蒙层仅覆盖 iframe 文档加载；合并中蒙层在 gogo 内
 */
export default defineComponent({
    name: 'AIConflictEditorView',
    props: {
        conflictItem: {
            type: Object,
            default: () => ({}),
        },
        currentBranchLabel: {
            type: String,
            default: '',
        },
        mergeBranchLabel: {
            type: String,
            default: '',
        },
    },
    emits: ['change'],
    setup(props, { emit }) {
        const { route } = useRouter();
        const iframeRef = ref(null);
        const showLoading = ref(true);
        let pendingSave = null;
        const buildIframeSrc = () => {
            const projectId = route.value?.query?.projectid || '';
            const branch = route.value?.query?.branch || '';
            const ours = props.conflictItem?.oursData?.mainBranch || '';
            const theirs = props.conflictItem?.theirsData?.mainBranch || '';
            const params = new URLSearchParams({
                projectid: projectId,
                branch,
                ours,
                theirs,
                oursName: props.currentBranchLabel,
                theirsName: props.mergeBranchLabel,
                // 新版 xy：推迟落盘到父页提交前
                deferSave: '1',
            });
            const basePath = BASE_PATH || '';
            return `${basePath.replace(/\/$/, '')}${aiMergeEditorRoutePath}?${params.toString()}`;
        };
        const iframeSrc = ref(buildIframeSrc());
        const clearPendingSave = () => {
            if (pendingSave) {
                clearTimeout(pendingSave.timer);
                pendingSave = null;
            }
        };
        /**
         * xy 提交前调用：命令 iframe 内 gogo 真保存，等待 mergeSuccess
         */
        const requestSave = () => new Promise((resolve, reject) => {
            const iframe = iframeRef.value;
            if (!iframe?.contentWindow) {
                reject(new Error($t('k_f10de695')));
                return;
            }
            clearPendingSave();
            pendingSave = {
                resolve,
                reject,
                timer: setTimeout(() => {
                    pendingSave = null;
                    reject(new Error($t('k_191b4dad')));
                }, 120000),
            };
            iframe.contentWindow.postMessage({
                source: AI_MERGE_HOST_SOURCE,
                type: 'requestSave',
                payload: {},
            }, '*');
        });
        const handleMessage = (event) => {
            const { data } = event;
            if (data?.source !== 'ai-editor-merge')
                return;
            switch (data.type) {
                case 'ready':
                    console.log('[AIConflictEditorView] Editor iframe ready:', data.payload);
                    break;
                case 'conflictResolved':
                    // 乐观已解决，但不回写 after（仍是冲突 YAML，无可用 tip）。
                    // 等 mergeSuccess 的 projectInfo 再更新 after / 展示代码变更。
                    emit('change', {
                        data: '',
                        resolved: data.payload?.success !== false,
                        awaitingProjectInfo: data.payload?.success !== false,
                    });
                    break;
                case 'mergeSuccess':
                    console.log('[AIConflictEditorView] Merge success:', data.payload);
                    if (data.payload?.projectInfo) {
                        const change = jsonToYaml(data.payload.projectInfo);
                        emit('change', {
                            data: change,
                            resolved: true,
                            awaitingProjectInfo: false,
                        });
                        if (pendingSave) {
                            const { resolve } = pendingSave;
                            clearPendingSave();
                            resolve(data.payload.projectInfo);
                        }
                    }
                    else {
                        // 无 projectInfo：结束等待，避免一直占着编辑器；有 pendingSave 则按失败处理
                        emit('change', {
                            data: '',
                            resolved: true,
                            awaitingProjectInfo: false,
                        });
                        if (pendingSave) {
                            const { reject } = pendingSave;
                            clearPendingSave();
                            reject(new Error($t('k_3781cdf2')));
                        }
                    }
                    break;
                case 'error':
                    console.error('[AIConflictEditorView] Editor error:', data.payload?.message);
                    if (pendingSave) {
                        const { reject } = pendingSave;
                        clearPendingSave();
                        reject(new Error(data.payload?.message || $t('k_6e133be7')));
                    }
                    break;
                default:
                    console.log('[AIConflictEditorView] Unknown message type:', data.type);
            }
            showLoading.value = false;
        };
        onMounted(() => {
            window.addEventListener('message', handleMessage);
        });
        onUnmounted(() => {
            window.removeEventListener('message', handleMessage);
            clearPendingSave();
        });
        return {
            iframeRef,
            iframeSrc,
            showLoading,
            requestSave,
        };
    },
    render() {
        return (<div class="w-full h-[calc(100vh-230px)] relative">
        <iframe ref="iframeRef" src={this.iframeSrc} class="w-full h-full border-0" sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-clipboard-read allow-clipboard-write"/>
        {this.showLoading && (<BuildingBlocksAnimation fullscreen text={$t('k_dce404e9')}/>)}
      </div>);
    },
});
